import React, { useMemo, useState } from "react";
import SalesTable from "../../InventoryManagement/SalesInvoice/SalesTable";
import Swal from "sweetalert2";
import useApi from "../../../../../utils/api-manager/Helper/useApi";
import { toast, ToastContainer } from "react-toastify";
import SalesInvoicePrint from "../../Dashboard/Invoice/SalesInvoicePrint";
import useFormattedDate from "../../../../../context/useFormattingUtils";
import useFormattingUtils from "../../../../../context/useFormattingUtils";
import InvoiceCsvExport from "./InvoiceCsvExport";

function SalesInvoiceHistory() {
  const { Delete, Post, GetPaginatedData } = useApi();

  const [showInvoice, setShowInvoice] = useState(false);
  const [modalData, setModalData] = useState(null);
  const [salesInvoiceData, setSalesInvoiceData] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [tableRefreshKey, setTableRefreshKey] = useState(0);
  const [showExportModal, setShowExportModal] = useState(false);

  const { formatDate } = useFormattedDate();
  const { formatCurrency } = useFormattingUtils();

  const safeCurrency = (value) => {
    const amount = parseFloat(value || 0);

    if (Number.isNaN(amount)) {
      return formatCurrency ? formatCurrency(0) : "0.00";
    }

    return formatCurrency ? formatCurrency(amount) : amount.toFixed(2);
  };

  const safeDate = (value) => {
    return value ? formatDate(value) : "N/A";
  };

  const getPaymentMethod = (value) => {
    const method = String(value || "N/A").trim();
    return method || "N/A";
  };

  const fetchSalesInvoiceData = async ({
    page = 1,
    searchTerm = "",
    sort = "",
  } = {}) => {
    const trimmedSearchTerm = String(searchTerm || searchQuery || "").trim();

    try {
      setLoading(true);

      const response = await GetPaginatedData("salesInvoiceData", {
        page,
        search: trimmedSearchTerm || undefined,
        queryParams: {
          invoice_type: "wholesale",
          ...(sort && { sort }),
        },
      });

      const results = Array.isArray(response?.data) ? response.data : [];
      const totalPages = Math.ceil((response?.totalCount || 0) / 10) || 1;

      const formatted = results.map((item) => ({
        ...item,
        date: item.date ? formatDate(item.date) : "N/A",
        raw_date: item.date,
      }));

      setSalesInvoiceData(formatted);

      return { data: formatted, totalPages };
    } catch (error) {
      console.error("Error fetching wholesale invoice data:", error);
      return { data: [], totalPages: 1 };
    } finally {
      setLoading(false);
    }
  };

  const fetchInvoiceDataForTable = async ({ page = 1, searchTerm = "" }) => {
    return await fetchSalesInvoiceData({
      page,
      searchTerm: searchTerm || searchQuery,
    });
  };

  const handleSearchSubmit = (e) => {
    if (e) {
      e.preventDefault();
    }

    setTableRefreshKey((prev) => prev + 1);
  };

  const handleClearSearch = () => {
    setSearchQuery("");
    setTableRefreshKey((prev) => prev + 1);
  };

  const handleRefresh = () => {
    setTableRefreshKey((prev) => prev + 1);
  };

  const fetchAllWholesaleInvoicesForExport = async () => {
    const pageSize = 100;
    const trimmedSearchTerm = String(searchQuery || "").trim();

    const firstResponse = await GetPaginatedData("salesInvoiceData", {
      page: 1,
      search: trimmedSearchTerm || undefined,
      queryParams: {
        invoice_type: "wholesale",
        page_size: pageSize,
      },
    });

    const firstRows = Array.isArray(firstResponse?.data) ? firstResponse.data : [];
    const totalPages = Math.ceil((firstResponse?.totalCount || firstRows.length) / pageSize) || 1;
    const rows = [...firstRows];

    for (let page = 2; page <= totalPages; page += 1) {
      const response = await GetPaginatedData("salesInvoiceData", {
        page,
        search: trimmedSearchTerm || undefined,
        queryParams: {
          invoice_type: "wholesale",
          page_size: pageSize,
        },
      });

      rows.push(...(Array.isArray(response?.data) ? response.data : []));
    }

    return rows.map((item) => ({
      ...item,
      raw_date: item.raw_date || item.date,
      date: item.date ? formatDate(item.date) : "N/A",
    }));
  };

  const handlePrint = async (invoiceId, openModal = false) => {
    try {
      setLoading(true);

      const response = await Post("getSalesInvoiceDetails", {
        invoice_id: invoiceId,
      });

      if (response?.data && openModal) {
        setModalData(response.data);
        setShowInvoice(true);
      }
    } catch (error) {
      console.error("Error fetching wholesale invoice details:", error);
      toast.error("Failed to open wholesale invoice preview.");
    } finally {
      setLoading(false);
    }
  };

  const handleCloseModal = () => {
    setShowInvoice(false);
    setModalData(null);
  };

  const handleDeleteProduct = async (invoiceId) => {
    const result = await Swal.fire({
      title: "Delete wholesale invoice?",
      text: "This will remove the wholesale invoice history record. This action cannot be reverted.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc2626",
      cancelButtonColor: "#4f46e5",
      confirmButtonText: "Yes, delete it",
      cancelButtonText: "Cancel",
    });

    if (!result.isConfirmed) return;

    try {
      await Delete("salesInvoiceData", invoiceId);

      setSalesInvoiceData((prevData) =>
        prevData.filter((invoice) => invoice.id !== invoiceId)
      );

      toast.success("Wholesale invoice deleted successfully.");
      setTableRefreshKey((prev) => prev + 1);
    } catch (error) {
      console.error("Error deleting wholesale invoice:", error);
      toast.error("Failed to delete wholesale invoice.");
    }
  };

  const stats = useMemo(() => {
    const invoiceCount = salesInvoiceData.length;

    const totalWholesaleSales = salesInvoiceData.reduce(
      (total, invoice) => total + (parseFloat(invoice.payable_amount) || 0),
      0
    );

    const uniqueCustomers = new Set(
      salesInvoiceData
        .map((invoice) => String(invoice.customer || "").trim())
        .filter(Boolean)
    );

    const cashCollected = salesInvoiceData
      .filter((invoice) => getPaymentMethod(invoice.pay_method).toLowerCase() === "cash")
      .reduce((total, invoice) => total + (parseFloat(invoice.payable_amount) || 0), 0);

    const accountOrDigitalCollected = salesInvoiceData
      .filter((invoice) => getPaymentMethod(invoice.pay_method).toLowerCase() !== "cash")
      .reduce((total, invoice) => total + (parseFloat(invoice.payable_amount) || 0), 0);

    const averageInvoice =
      invoiceCount > 0 ? totalWholesaleSales / invoiceCount : 0;

    return {
      invoiceCount,
      totalWholesaleSales,
      uniqueCustomerCount: uniqueCustomers.size,
      cashCollected,
      accountOrDigitalCollected,
      averageInvoice,
    };
  }, [salesInvoiceData]);

  const columns = [
    {
      field: "invoice_no",
      header: "Invoice No",
      body: (rowData) => (
        <div className="wih-invoice-cell">
          <div className="wih-invoice-no">{rowData.invoice_no || "-"}</div>
          <div className="wih-invoice-subtitle">Wholesale invoice</div>
        </div>
      ),
    },
    {
      field: "customer",
      header: "Customer",
      body: (rowData) => (
        <div className="wih-customer-cell">
          <div className="wih-customer-name">
            {rowData.customer || rowData.customer_name || "Walk-in / Unassigned"}
          </div>
          <div className="wih-customer-subtitle">B2B sales customer</div>
        </div>
      ),
    },
    {
      field: "payable_amount",
      header: "Invoice Value",
      body: (rowData) => (
        <span className="wih-money-pill">
          {safeCurrency(rowData.payable_amount)}
        </span>
      ),
    },
    {
      field: "pay_method",
      header: "Collection Mode",
      body: (rowData) => {
        const method = getPaymentMethod(rowData.pay_method);
        const normalized = method.toLowerCase();

        let className = "wih-pay-pill wih-pay-other";

        if (normalized === "cash") {
          className = "wih-pay-pill wih-pay-cash";
        } else if (normalized.includes("bank") || normalized.includes("card")) {
          className = "wih-pay-pill wih-pay-bank";
        }

        return <span className={className}>{method}</span>;
      },
    },
    {
      field: "date",
      header: "Invoice Date",
      body: (rowData) => (
        <div className="wih-date-cell">
          {rowData.raw_date ? safeDate(rowData.raw_date) : rowData.date || "N/A"}
        </div>
      ),
    },
  ];

  const exportColumns = [
    { key: "invoice_no", label: "Invoice No", getValue: (invoice) => invoice.invoice_no || "" },
    { key: "date", label: "Invoice Date", getValue: (invoice) => safeDate(invoice.raw_date || invoice.date) },
    { key: "customer", label: "Customer", getValue: (invoice) => invoice.customer || invoice.customer_name || "Walk-in / Unassigned" },
    { key: "payable_amount", label: "Invoice Value", getValue: (invoice) => parseFloat(invoice.payable_amount || 0).toFixed(2) },
    { key: "pay_method", label: "Collection Mode", getValue: (invoice) => getPaymentMethod(invoice.pay_method) },
    { key: "created_at", label: "Created At", getValue: (invoice) => invoice.created_at || "" },
  ];

  const renderStatCard = ({ title, value, subtitle, icon, tone = "default" }) => (
    <div className={`wih-stat-card wih-stat-${tone}`}>
      <div className="wih-stat-topline">
        <span className="wih-stat-icon">{icon}</span>
        <span>{title}</span>
      </div>
      <div className="wih-stat-value">{value}</div>
      {subtitle && <div className="wih-stat-subtitle">{subtitle}</div>}
    </div>
  );

  return (
    <div>
      <ToastContainer />

      <style>
        {`
          .wih-page {
            background:
              radial-gradient(circle at top left, rgba(79, 70, 229, 0.12), transparent 34%),
              radial-gradient(circle at top right, rgba(14, 165, 233, 0.10), transparent 34%),
              linear-gradient(180deg, #f8fafc 0%, #ffffff 100%);
            min-height: 100vh;
            padding: 6px 0 24px;
          }

          .wih-hero {
            background:
              linear-gradient(135deg, #312e81 0%, #4338ca 58%, #1e40af 100%);
            border-radius: 26px;
            padding: 26px;
            color: #ffffff;
            margin-bottom: 18px;
            box-shadow: 0 22px 50px rgba(49, 46, 129, 0.22);
            position: relative;
            overflow: hidden;
          }

          .wih-hero::before {
            content: "";
            position: absolute;
            width: 280px;
            height: 280px;
            right: -95px;
            top: -115px;
            border-radius: 50%;
            background: rgba(255, 255, 255, 0.13);
          }

          .wih-hero::after {
            content: "";
            position: absolute;
            width: 180px;
            height: 180px;
            right: 120px;
            bottom: -105px;
            border-radius: 50%;
            background: rgba(125, 211, 252, 0.16);
          }

          .wih-hero-content {
            position: relative;
            z-index: 1;
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            gap: 22px;
            flex-wrap: wrap;
          }

          .wih-badge {
            display: inline-flex;
            align-items: center;
            gap: 8px;
            background: rgba(255, 255, 255, 0.14);
            border: 1px solid rgba(255, 255, 255, 0.26);
            color: #e0e7ff;
            padding: 8px 13px;
            border-radius: 999px;
            font-weight: 950;
            font-size: 12px;
            margin-bottom: 13px;
          }

          .wih-title {
            margin: 0;
            font-size: 31px;
            font-weight: 950;
            letter-spacing: -0.04em;
            line-height: 1.08;
          }

          .wih-description {
            margin: 9px 0 0;
            color: rgba(255, 255, 255, 0.82);
            font-size: 14px;
            max-width: 680px;
            line-height: 1.55;
          }

          .wih-hero-actions {
            display: flex;
            gap: 10px;
            flex-wrap: wrap;
            justify-content: flex-end;
          }

          .wih-primary-btn,
          .wih-secondary-btn,
          .wih-light-btn {
            border: none;
            border-radius: 999px;
            padding: 10px 16px;
            font-size: 13px;
            font-weight: 950;
            cursor: pointer;
            transition: transform 0.2s ease, box-shadow 0.2s ease, opacity 0.2s ease;
            white-space: nowrap;
          }

          .wih-primary-btn:hover,
          .wih-secondary-btn:hover,
          .wih-light-btn:hover {
            transform: translateY(-1px);
          }

          .wih-light-btn {
            background: #ffffff;
            color: #312e81;
            box-shadow: 0 10px 24px rgba(15, 23, 42, 0.16);
          }

          .wih-secondary-btn {
            background: rgba(255, 255, 255, 0.12);
            color: #ffffff;
            border: 1px solid rgba(255, 255, 255, 0.26);
          }

          .wih-primary-btn {
            background: #4f46e5;
            color: #ffffff;
          }

          .wih-btn-disabled {
            opacity: 0.62;
            cursor: not-allowed;
          }

          .wih-stats-grid {
            display: grid;
            grid-template-columns: repeat(4, minmax(0, 1fr));
            gap: 14px;
            margin-bottom: 18px;
          }

          .wih-stat-card {
            background: rgba(255, 255, 255, 0.94);
            border: 1px solid #e5e7eb;
            border-radius: 20px;
            padding: 17px 18px;
            box-shadow: 0 12px 32px rgba(15, 23, 42, 0.06);
            backdrop-filter: blur(8px);
          }

          .wih-stat-sales {
            background: linear-gradient(180deg, #eef2ff 0%, #ffffff 100%);
            border-color: #c7d2fe;
          }

          .wih-stat-customers {
            background: linear-gradient(180deg, #f0f9ff 0%, #ffffff 100%);
            border-color: #bae6fd;
          }

          .wih-stat-collection {
            background: linear-gradient(180deg, #ecfdf5 0%, #ffffff 100%);
            border-color: #bbf7d0;
          }

          .wih-stat-average {
            background: linear-gradient(180deg, #fff7ed 0%, #ffffff 100%);
            border-color: #fed7aa;
          }

          .wih-stat-topline {
            display: flex;
            align-items: center;
            gap: 8px;
            font-size: 11px;
            color: #64748b;
            font-weight: 950;
            text-transform: uppercase;
            letter-spacing: 0.055em;
          }

          .wih-stat-icon {
            width: 28px;
            height: 28px;
            border-radius: 10px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            background: #f1f5f9;
            font-size: 15px;
          }

          .wih-stat-value {
            margin-top: 10px;
            font-size: 25px;
            font-weight: 950;
            color: #0f172a;
            line-height: 1;
            word-break: break-word;
          }

          .wih-stat-subtitle {
            margin-top: 8px;
            font-size: 12px;
            color: #64748b;
            font-weight: 750;
          }

          .wih-table-card {
            background: #ffffff;
            border: 1px solid #e5e7eb;
            border-radius: 24px;
            padding: 18px;
            box-shadow: 0 16px 42px rgba(15, 23, 42, 0.07);
          }

          .wih-table-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            gap: 16px;
            flex-wrap: wrap;
            margin-bottom: 18px;
          }

          .wih-section-title {
            margin: 0;
            font-size: 21px;
            font-weight: 950;
            color: #0f172a;
            letter-spacing: -0.02em;
          }

          .wih-section-subtitle {
            margin: 5px 0 0;
            color: #64748b;
            font-size: 12px;
            font-weight: 750;
          }

          .wih-search-form {
            display: flex;
            align-items: center;
            gap: 10px;
            flex-wrap: wrap;
          }

          .wih-search-wrap {
            position: relative;
          }

          .wih-search-icon {
            position: absolute;
            left: 14px;
            top: 50%;
            transform: translateY(-50%);
            color: #94a3b8;
            font-size: 14px;
          }

          .wih-search-input {
            width: 305px;
            height: 42px;
            border: 1px solid #d1d5db;
            border-radius: 999px;
            padding: 0 16px 0 38px;
            font-size: 13px;
            outline: none;
            font-weight: 750;
            color: #111827;
            background: #ffffff;
            transition: border 0.2s ease, box-shadow 0.2s ease;
          }

          .wih-search-input:focus {
            border-color: #818cf8;
            box-shadow: 0 0 0 4px rgba(129, 140, 248, 0.16);
          }

          .wih-table-shell {
            border: 1px solid #e5e7eb;
            border-radius: 18px;
            overflow: hidden;
            background: #ffffff;
          }

          .wih-invoice-cell,
          .wih-customer-cell {
            display: flex;
            flex-direction: column;
            gap: 3px;
          }

          .wih-invoice-no,
          .wih-customer-name {
            font-weight: 950;
            color: #0f172a;
          }

          .wih-invoice-subtitle,
          .wih-customer-subtitle {
            font-size: 11px;
            color: #64748b;
            font-weight: 700;
          }

          .wih-money-pill {
            display: inline-flex;
            background: #eef2ff;
            color: #4338ca;
            border: 1px solid #c7d2fe;
            border-radius: 999px;
            padding: 7px 11px;
            font-weight: 950;
            font-size: 12px;
            white-space: nowrap;
          }

          .wih-pay-pill {
            display: inline-flex;
            border-radius: 999px;
            padding: 7px 11px;
            font-weight: 950;
            font-size: 12px;
            text-transform: capitalize;
            white-space: nowrap;
          }

          .wih-pay-cash {
            background: #ecfdf5;
            color: #047857;
            border: 1px solid #a7f3d0;
          }

          .wih-pay-bank {
            background: #eff6ff;
            color: #1d4ed8;
            border: 1px solid #bfdbfe;
          }

          .wih-pay-other {
            background: #f5f3ff;
            color: #6d28d9;
            border: 1px solid #ddd6fe;
          }

          .wih-date-cell {
            font-weight: 850;
            color: #0f172a;
            white-space: nowrap;
          }

          @media (max-width: 1200px) {
            .wih-stats-grid {
              grid-template-columns: repeat(2, minmax(0, 1fr));
            }
          }

          @media (max-width: 768px) {
            .wih-page {
              padding: 0 0 18px;
            }

            .wih-hero {
              border-radius: 18px;
              padding: 20px;
            }

            .wih-title {
              font-size: 25px;
            }

            .wih-hero-actions,
            .wih-search-form {
              width: 100%;
              justify-content: stretch;
            }

            .wih-light-btn,
            .wih-secondary-btn,
            .wih-primary-btn {
              flex: 1;
            }

            .wih-stats-grid {
              grid-template-columns: 1fr;
            }

            .wih-table-card {
              padding: 14px;
              border-radius: 18px;
            }

            .wih-table-header {
              align-items: stretch;
            }

            .wih-search-wrap,
            .wih-search-input {
              width: 100%;
            }

            .wih-primary-btn,
            .wih-secondary-btn {
              width: 100%;
            }
          }

          @media (max-width: 480px) {
            .wih-stat-value {
              font-size: 22px;
            }

            .wih-table-shell {
              overflow-x: auto;
            }
          }
        `}
      </style>

      <div className="responsive-box wih-page">
        <div className="wih-hero">
          <div className="wih-hero-content">
            <div>
              <div className="wih-badge">
                <span>●</span>
                <span>Wholesale Account Sales</span>
              </div>

              <h2 className="wih-title">Wholesale Invoice History</h2>

              <p className="wih-description">
                Manage bulk product sales, B2B customers, payment collections and professional wholesale invoice prints.
              </p>
            </div>

            <div className="wih-hero-actions">
              <button
                type="button"
                onClick={handleRefresh}
                disabled={loading}
                className={`wih-light-btn ${loading ? "wih-btn-disabled" : ""}`}
              >
                {loading ? "Refreshing..." : "Refresh Invoices"}
              </button>

              <button
                type="button"
                onClick={() => {
                  setSearchQuery("");
                  setTableRefreshKey((prev) => prev + 1);
                }}
                className="wih-secondary-btn"
              >
                Reset View
              </button>
            </div>
          </div>
        </div>

        <div className="wih-stats-grid">
          {renderStatCard({
            title: "Wholesale Invoices",
            value: stats.invoiceCount,
            subtitle: "Current table result",
            icon: "🧾",
          })}

          {renderStatCard({
            title: "Wholesale Sales",
            value: safeCurrency(stats.totalWholesaleSales),
            subtitle: "Total invoice value loaded",
            icon: "₹",
            tone: "sales",
          })}

          {renderStatCard({
            title: "B2B Customers",
            value: stats.uniqueCustomerCount,
            subtitle: "Unique customers loaded",
            icon: "🏢",
            tone: "customers",
          })}

          {renderStatCard({
            title: "Average Invoice",
            value: safeCurrency(stats.averageInvoice),
            subtitle: "Per wholesale invoice",
            icon: "📊",
            tone: "average",
          })}
        </div>

        <div className="wih-table-card">
          <div className="wih-table-header">
            <div>
              <h3 className="wih-section-title">Wholesale Invoice List</h3>
              <p className="wih-section-subtitle">
                Search, preview, print and manage professional wholesale invoices.
              </p>
            </div>

            <form onSubmit={handleSearchSubmit} className="wih-search-form">
              <div className="wih-search-wrap">
                <span className="wih-search-icon">⌕</span>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search invoice, customer, payment..."
                  className="wih-search-input"
                />
              </div>

              <button type="submit" className="wih-primary-btn">
                Search
              </button>

              <button
                type="button"
                onClick={() => setShowExportModal(true)}
                className="wih-primary-btn"
                style={{ background: "#0891b2" }}
              >
                Export CSV
              </button>

              {searchQuery && (
                <button
                  type="button"
                  onClick={handleClearSearch}
                  className="wih-secondary-btn"
                  style={{
                    color: "#4338ca",
                    borderColor: "#c7d2fe",
                    background: "#eef2ff",
                  }}
                >
                  Clear
                </button>
              )}
            </form>
          </div>

          <div className="wih-table-shell">
            <SalesTable
              key={`${tableRefreshKey}-${searchQuery}`}
              fetchData={fetchInvoiceDataForTable}
              columns={columns}
              showAction={true}
              onDelete={handleDeleteProduct}
              handlePrint={(invoiceId) => handlePrint(invoiceId, true)}
              enablePagination={true}
              rowsPerPage={10}
              showSearch={false}
            />
          </div>
        </div>

        <InvoiceCsvExport
          isOpen={showExportModal}
          onClose={() => setShowExportModal(false)}
          title="Wholesale CSV Export"
          columns={exportColumns}
          fetchRows={fetchAllWholesaleInvoicesForExport}
          getRowDate={(invoice) => invoice.raw_date || invoice.date}
          filenamePrefix="wholesale-invoice-report"
          accent="#4f46e5"
        />

        {showInvoice && (
          <SalesInvoicePrint
            open={showInvoice}
            onClose={handleCloseModal}
            data={modalData}
            onPrint={handlePrint}
          />
        )}
      </div>
    </div>
  );
}

export default SalesInvoiceHistory;
